import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Stack,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { SubmitHandler, useForm } from 'react-hook-form';

import { faEye, faFileText } from '@fortawesome/free-regular-svg-icons';
import {
  faClipboardCheck,
  faGear,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import companyLogoImg from 'src/assets/img/company-logo.png';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { CameraMode } from 'src/models/CameraMode.model';
import {
  CustomField,
  CustomPracticeProfileFieldEnum,
  CustomUserProfileFieldEnum,
} from 'src/models/GalenData/CustomField.model';
import { CustomFieldData } from 'src/models/GalenData/CustomFieldData.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import {
  convertPracticeSettings,
  convertUserSettings,
  getPracticeDirtyFields,
} from 'src/models/GalenData/helpers/saveSettings';
import { adminRoleList } from 'src/models/GalenData/UserRole.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { Product } from 'src/models/Product.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useGetUsers from 'src/hooks/useGetUsers';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import CameraSettings from './CameraSettingsTab';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB in bytes
const MAX_FILES = 1;

enum TabEnum {
  Camera = 'Camera',
  Application = 'Application',
}

export type ImagePreviewTypes = { preview: string } & File;

export type SettingFormDataTypes = {
  imageFolder: string | null;
  watermark: string | null;
  shouldShowGender: boolean;
  shouldShowHeatmap: boolean;
  shouldShowCompanyLogo: boolean;
  hasMfa: boolean;
};

interface ISettingsTabProps {
  ipList: string[];
  setIpList: Dispatch<SetStateAction<string[]>>;
}

const SettingsTab = ({ ipList, setIpList }: ISettingsTabProps) => {
  const { tPages, tCommon, language } = useUiTranslation();
  const {
    practiceSettings: { product },
    setImageFolder,
    cameraMode,
    setPracticeSettings,
    setUserSettings,
  } = useRemoteBoundStore();

  const { currentUser, userId, practiceId, userType } = useGetCurrentUser();

  const [key, setKey] = useState<TabEnum>(TabEnum.Camera);
  const [userSettingsRawData, setUserSettingsRawData] =
    useState<CustomFieldData<CustomUserProfileFieldEnum>[]>();

  const [practiceSettingsRawData, setPracticeSettingsRawData] =
    useState<CustomFieldData<CustomPracticeProfileFieldEnum>[]>();

  const [practiceCustomFields, setPracticeCustomFields] = useState<
    CustomField<CustomPracticeProfileFieldEnum>[]
  >([]);

  const [userCustomFields, setUserCustomFields] = useState<
    CustomField<CustomUserProfileFieldEnum>[]
  >([]);

  const { getErrorMessage } = useGetGalenErrorMsg();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const fetchCustomFieldDefinitions = async () => {
    try {
      const [practiceCustomFieldsResponse, userCustomFieldsResponse] =
        await Promise.all([
          Api.remote.getPracticeCustomFieldDefinitions(),
          Api.remote.getUserCustomFieldDefinitions(),
        ]);
      setPracticeCustomFields(practiceCustomFieldsResponse.data.content ?? []);
      setUserCustomFields(userCustomFieldsResponse.data.content ?? []);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: `${tCommon('message.error.failedToLoadSettings')} ${errorMessage}`,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      setPracticeCustomFields([]);
      setUserCustomFields([]);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields, isLoading, isSubmitting },
  } = useForm({
    defaultValues: async () => {
      try {
        if (!practiceId) {
          throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_ID_REQUIRED));
        }

        if (!userId) {
          throw new Error(tCommon('message.error.userIdNotFound'));
        }

        await fetchCustomFieldDefinitions();

        const [userSettings, practiceSettings] = await Promise.all([
          Api.remote.getUserSettings(userId),
          Api.remote.getPracticeSettings(practiceId),
        ]);

        setUserSettingsRawData(userSettings.data);
        setPracticeSettingsRawData(practiceSettings.data);

        const logoId = practiceSettings.data.find(
          (setting) =>
            setting.field.name === CustomPracticeProfileFieldEnum.LogoFile,
        )?.fieldData;

        if (logoId) {
          const logo = await Api.remote.getLogoFile({
            deviceDataId: practiceSettings.data.find(
              (setting) =>
                setting.field.name === CustomPracticeProfileFieldEnum.LogoFile,
            )?.fieldData,
          });

          if (logo.data.Logo) {
            setLogoFiles([
              {
                ...logo.data.Logo,
                preview: logo.data.Logo.link,
              },
            ]);
          }
        }

        const practiceSettingsData = convertPracticeSettings(
          practiceSettings.data,
        );

        return {
          imageFolder: userSettings.data.find(
            (setting) =>
              setting.field.name === CustomUserProfileFieldEnum.ImageFolder,
          )?.fieldData,
          ...practiceSettingsData,
        };
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: `${tCommon('message.error.failedToLoadSettings')} ${errorMessage}`,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        throw error;
      }
    },
  });

  const [logoFiles, setLogoFiles] = useState<ImagePreviewTypes[]>([]);

  const onSubmit: SubmitHandler<{
    imageFolder: string | null;
    watermark: string | null;
    shouldShowGender: boolean;
    shouldShowHeatmap: boolean;
    shouldShowCompanyLogo: boolean;
    hasMfa: boolean;
  }> = async (data) => {
    try {
      if (!currentUser?.currentRole?.practice) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      const logoId = hasUpdatedCompanyLogo
        ? await Api.remote.getLogoId(logoFiles)
        : undefined;

      if (dirtyFields.imageFolder && userSettingsRawData) {
        await Api.remote.saveUserSettings({
          userSettingsData: userSettingsRawData,
          data: {
            [CustomUserProfileFieldEnum.ImageFolder]: data.imageFolder,
          },
          user: currentUser,
          userCustomFields,
        });

        setImageFolder(data.imageFolder);
      }

      const practiceDirtyFields = getPracticeDirtyFields(dirtyFields);

      if (practiceDirtyFields.length > 0 || logoId) {
        await Api.remote.savePracticeSettings(
          practiceDirtyFields,
          practiceSettingsRawData,
          data,
          currentUser.currentRole.practice,
          logoId,
          practiceCustomFields,
        );
      }

      if (logoId) {
        setHasUpdatedCompanyLogo(false);
      }

      reset({ ...data });

      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_ID_REQUIRED));
      }

      if (!userId) {
        throw new Error(getErrorMessage(GalenErrorKeys.USER_ID_REQUIRED));
      }

      await fetchCustomFieldDefinitions();

      const [userSettings, practiceSettings] = await Promise.all([
        Api.remote.getUserSettings(userId),
        Api.remote.getPracticeSettings(practiceId),
      ]);

      const userSettingsData = convertUserSettings(userSettings.data);
      const practiceSettingsData = convertPracticeSettings(
        practiceSettings.data,
      );

      setUserSettings({ userSettings: userSettingsData });
      setPracticeSettings({ practiceSettings: practiceSettingsData });

      Api.alertBox({
        title: tCommon('bootbox.title.Success'),
        message: tCommon('message.success.updateSettings'),
        locale: getLocaleCode(language as Language),
        type: 'success',
      });
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const { handleGetUsers } = useGetUsers();

  const handleBulkMFA = async (MFA: boolean) => {
    try {
      const fetchedUsers = await handleGetUsers();
      const promises =
        fetchedUsers.content?.map((user) =>
          Api.remote.set_user_mfa(MFA, user.userId),
        ) || [];
      await Promise.all(promises);

      Api.alertBox({
        title: tCommon('bootbox.title.Information'),
        message: tPages('settings.applicationForm.fields.MFAbulk.success'),
        locale: getLocaleCode(language as Language),
        type: 'success',
      });
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const isAssurePlus = product.includes(Product.ASSUREPLUS);
  const isClient = Api.isClient();

  const isAdmin = userType && adminRoleList.includes(userType);

  const handleSave = handleSubmit(onSubmit);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    multiple: false,
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setHasUpdatedCompanyLogo(true);
      setLogoFiles(
        acceptedFiles.map((file) => {
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
          });
        }),
      );
    },
    validator(file) {
      if (file.size > MAX_FILE_SIZE) {
        const errorMessage = tCommon('message.error.fileTooLarge');
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        return {
          code: 'file-too-large',
          message: errorMessage,
        };
      }
      if (
        file.type !== 'image/png' &&
        file.type !== 'image/jpeg' &&
        file.type !== 'image/jpg'
      ) {
        const errorMessage = tCommon('message.error.fileNotPng');
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        return {
          code: 'file-type-invalid',
          message: errorMessage,
        };
      }

      return null;
    },
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      for (const file of logoFiles) URL.revokeObjectURL(file.preview);
    };
  }, [logoFiles]);

  const [hasUpdatedCompanyLogo, setHasUpdatedCompanyLogo] = useState(false);

  return (
    <div className="mb-4 overflow-y-auto overflow-x-hidden">
      <div className="d-flex justify-content-between align-items-center">
        <h3>
          {tPages(`settings.tabs.${key}`)} {tPages('settings.title')}
        </h3>

        {(key === TabEnum.Application ||
          (isClient && cameraMode === CameraMode.UPLOAD)) && (
          <div>
            <Button
              onClick={handleSave}
              variant="primary"
              disabled={(!isDirty && !hasUpdatedCompanyLogo) || isSubmitting}
            >
              <FontAwesomeIcon icon={faGear} className="me-2" />
              {isSubmitting
                ? `${tPages('settings.applicationForm.saving')}...`
                : tPages('settings.applicationForm.saveSettings')}
            </Button>
          </div>
        )}
      </div>

      <Tabs
        id="controlled-tab-example"
        activeKey={key}
        onSelect={(k) => {
          setKey(k as TabEnum);
          reset();
        }}
        className="mt-3"
      >
        <Tab
          eventKey={TabEnum.Camera}
          title={tPages(`settings.tabs.Camera`)}
          tabClassName="text-primary"
        >
          <CameraSettings
            ipList={ipList}
            setIpList={setIpList}
            registerImageFolder={register('imageFolder')}
            isLoading={isLoading}
          />
        </Tab>

        {isAdmin && (
          <Tab
            eventKey={TabEnum.Application}
            title={tPages(`settings.tabs.Application`)}
            className="pt-3"
            tabClassName="text-primary"
          >
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 400 }}
                  >
                    <Spinner variant="primary" />
                  </div>
                ) : (
                  <>
                    <Col md={6}>
                      <Card className="data-container mt-2 p-0 shadow">
                        <Card.Body>
                          <Form.Group>
                            <Form.Label>
                              <FontAwesomeIcon
                                icon={faFileText}
                                className="me-2"
                              />
                              <strong>
                                {tPages(
                                  `settings.applicationForm.fields.watermarkText.title`,
                                )}
                              </strong>
                            </Form.Label>
                            <Form.Control
                              id="watermark"
                              type="text"
                              placeholder={tPages(
                                `settings.applicationForm.fields.watermarkText.placeholder`,
                              )}
                              {...register('watermark')}
                              disabled={!isAdmin}
                              autoComplete="off"
                            />
                            <small className="text-primary">
                              {tPages(
                                `settings.applicationForm.fields.watermarkText.desc`,
                              )}
                            </small>
                          </Form.Group>

                          {isAdmin && (
                            <div>
                              <Form.Group className="pt-4">
                                <Form.Label>
                                  <FontAwesomeIcon
                                    icon={faLock}
                                    className="me-2"
                                  />
                                  <strong>
                                    {tPages(
                                      'settings.applicationForm.fields.MFA.title',
                                    )}
                                  </strong>
                                </Form.Label>
                                <Form.Check
                                  type="switch"
                                  id="hasMfa"
                                  label={tPages(
                                    'settings.applicationForm.fields.MFA.field',
                                  )}
                                  {...register('hasMfa')}
                                  disabled={!isAdmin}
                                />
                                <small className="text-primary">
                                  {tPages(
                                    'settings.applicationForm.fields.MFA.desc',
                                  )}
                                </small>
                              </Form.Group>
                              <div className="d-flex mt-4 gap-4">
                                <Button
                                  variant="primary"
                                  onClick={() => {
                                    Api.confirmBox({
                                      title: tCommon('bootbox.title.Confirm'),
                                      message: tPages(
                                        'settings.applicationForm.fields.MFAbulk.confirmOn',
                                      ),
                                      locale: getLocaleCode(
                                        language as Language,
                                      ),
                                      callback: (result) => {
                                        if (result) {
                                          handleBulkMFA(true);
                                        }
                                      },
                                    });
                                  }}
                                >
                                  {tPages(
                                    'settings.applicationForm.fields.MFAbulk.on',
                                  )}
                                </Button>
                                <Button
                                  variant="danger"
                                  onClick={() => {
                                    Api.confirmBox({
                                      title: tCommon('bootbox.title.Confirm'),
                                      message: tPages(
                                        'settings.applicationForm.fields.MFAbulk.confirmOff',
                                      ),
                                      locale: getLocaleCode(
                                        language as Language,
                                      ),
                                      callback: (result) => {
                                        if (result) {
                                          handleBulkMFA(false);
                                        }
                                      },
                                    });
                                  }}
                                >
                                  {tPages(
                                    'settings.applicationForm.fields.MFAbulk.off',
                                  )}
                                </Button>
                              </div>
                              <small className="text-primary">
                                {tPages(
                                  'settings.applicationForm.fields.MFAbulk.desc',
                                )}
                              </small>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    {isAssurePlus && (
                      <>
                        <Col md={6}>
                          <Card className="data-container mt-2 p-0 shadow">
                            <Card.Body>
                              <Form.Group>
                                <Form.Label>
                                  <FontAwesomeIcon
                                    icon={faClipboardCheck}
                                    className="me-2"
                                  />
                                  <strong>
                                    {tPages(
                                      `settings.applicationForm.fields.reportFields.title`,
                                    )}
                                  </strong>
                                </Form.Label>

                                <Form.Check
                                  type="switch"
                                  id="shouldShowGender"
                                  label={tPages(
                                    `settings.applicationForm.fields.reportFields.fields.patientGender`,
                                  )}
                                  {...register('shouldShowGender')}
                                  disabled={!isAdmin}
                                />
                                <Form.Check
                                  type="switch"
                                  id="shouldShowHeatmap"
                                  label={tPages(
                                    `settings.applicationForm.fields.reportFields.fields.heatmap`,
                                  )}
                                  {...register('shouldShowHeatmap')}
                                  disabled={!isAdmin}
                                />
                                <Form.Check
                                  type="switch"
                                  id="shouldShowCompanyLogo"
                                  label={tPages(
                                    `settings.applicationForm.fields.reportFields.fields.companyLogo`,
                                  )}
                                  {...register('shouldShowCompanyLogo')}
                                  disabled={!isAdmin}
                                />
                                <small className="text-primary">
                                  {tPages(
                                    `settings.applicationForm.fields.reportFields.desc`,
                                  )}
                                </small>
                              </Form.Group>

                              <Form.Group className="mt-4 pt-4">
                                <Form.Label>
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className="me-2"
                                  />
                                  <strong>
                                    {tPages(
                                      `settings.applicationForm.fields.companyLogo.title`,
                                    )}
                                  </strong>
                                </Form.Label>

                                <Stack gap={3}>
                                  {logoFiles.length > 0 ? (
                                    logoFiles.map((file) => (
                                      <div
                                        className="logo_report-company"
                                        key={file.preview}
                                      >
                                        <img
                                          className="mw-100 object-fit-contain"
                                          src={file.preview}
                                          alt={tPages(
                                            'settings.applicationForm.fields.companyLogo.title',
                                          )}
                                          onLoad={() => {
                                            URL.revokeObjectURL(file.preview);
                                          }}
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <div className="logo_report-default">
                                      <img
                                        src={companyLogoImg}
                                        alt={tPages(
                                          'settings.applicationForm.fields.companyLogo.title',
                                        )}
                                      />
                                    </div>
                                  )}

                                  {isAdmin && (
                                    <div
                                      {...getRootProps({
                                        className: 'dropzone',
                                      })}
                                    >
                                      <input {...getInputProps()} />

                                      {isDragActive ? (
                                        <p>
                                          {tPages(
                                            `settings.applicationForm.fields.companyLogo.dropText`,
                                          )}
                                          ...
                                        </p>
                                      ) : (
                                        <p>
                                          {tPages(
                                            `settings.applicationForm.fields.companyLogo.desc`,
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </Stack>
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                      </>
                    )}
                  </>
                )}
              </Row>
            </Form>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsTab;
